import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiError, setInMemoryToken, setSessionExpiredHandler } from "@/lib/api";

const AuthCtx = createContext(null);

/**
 * AuthContext — user session ONLY.
 *
 * iter83 rewrite: this context no longer touches sessionStorage.
 * Brand slug is now the SOLE responsibility of BrandContext, which is
 * driven exclusively by the URL. The user's `pub_slug` still flows
 * through this context (from the auth API responses), and downstream
 * code that needs it (RootRoute post-login redirect, LoginPage) reads
 * it straight off the `user` object — no client-side cache.
 */

export function AuthProvider({ children }) {
  // null = unknown (initial), false = unauthenticated, object = authenticated user
  const [user, setUser] = useState(null);
  const [bootstrapped, setBootstrapped] = useState(false);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setBootstrapped(true);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  // iter176 — Register the 401 session-expired handler with the shared
  // axios client. On any 401 (except auth handshake paths), clear our
  // token, drop user to "signed out", and navigate to the correct
  // login surface with `?session=expired` so LoginPage can show the
  // friendly toast AFTER navigation (we can't toast BEFORE navigating
  // because window.location.replace tears down the DOM before Sonner
  // paints — verified by QA agent).
  //
  // Branded routes redirect to `/[slug]` (the SluggedLoginPage that
  // App.js actually declares) — NOT `/c/[slug]/login` which has no
  // route and would fall through two accidental catch-all redirects.
  useEffect(() => {
    // Reserved single-segment master paths that must NOT be mistaken for
    // a branded login (`/[slug]`) by the already-on-login idempotence
    // guard below. Kept in lock-step with `RESERVED_ROOTS` in
    // `LoginPage.jsx` and `BrandContext.jsx`.
    const RESERVED_SEGMENTS = new Set([
      "login", "register", "reset", "terms", "privacy", "cookies",
      "unsubscribe", "business", "redeem", "c", "client",
      "editor", "settings", "admin",
      "leaderboard", "leagues", "matches",
      "champions", "seasons-archive", "payments",
    ]);
    setSessionExpiredHandler(() => {
      setInMemoryToken(null);
      setUser(false);
      if (typeof window === "undefined") return;
      const path = window.location.pathname || "";
      const brandedMatch = path.match(/^\/c\/([^/]+)(?:\/|$)/);
      const target = brandedMatch ? `/${brandedMatch[1]}` : "/login";
      // Idempotence: don't loop if already on a login-ish page.
      //   - Master login is `/login`.
      //   - Branded /c/[slug]/... routes: idempotent when the current
      //     path is exactly `/${slug}` (SluggedLoginPage).
      //   - Bare `/[slug]` (SluggedLoginPage) is a single segment. We
      //     recognise it ONLY when the segment is NOT a reserved
      //     master route, otherwise `/admin` / `/editor` etc. would
      //     match this branch and swallow the redirect.
      const bareSegMatch = path.match(/^\/([^/]+)\/?$/);
      const isBareSlugLogin =
        bareSegMatch && !RESERVED_SEGMENTS.has(bareSegMatch[1].toLowerCase());
      const alreadyOnLogin =
        path.endsWith("/login") ||
        (brandedMatch && path === `/${brandedMatch[1]}`) ||
        isBareSlugLogin;
      if (alreadyOnLogin) return;
      const sep = target.includes("?") ? "&" : "?";
      window.location.replace(`${target}${sep}session=expired`);
    });
    return () => setSessionExpiredHandler(null);
  }, []);

  const login = useCallback(async (email, password, opts = {}) => {
    const { masterOnly = false } = opts;
    try {
      const { data } = await api.post("/auth/login", { email, password });
      // iter79 — master-portal lockdown. When a non-admin authenticates on
      // a masterOnly surface (`/`, `/login`, `/reset/:token`), we must NEVER
      // persist their session to context, otherwise RootRoute would react
      // to the state change and redirect them to `/c/[slug]`, unmounting
      // the LoginPage before it can render the SUPER-ADMINS ONLY banner.
      // Instead: invalidate the freshly-minted token server-side, discard
      // it client-side, and return a distinct `master_locked` error the
      // caller can render inline.
      if (masterOnly && data?.user?.role !== "admin") {
        try {
          // Best-effort cookie cleanup — the token was just set on the
          // response. If this fails we still don't cache it locally.
          await api.post("/auth/logout");
        } catch {
          /* noop — server may already have expired the cookie */
        }
        return { ok: false, error: "master_locked", master_locked: true, user: data.user };
      }
      // Cache the issued token in memory as a Bearer fallback (also set as httpOnly cookie by backend).
      setInMemoryToken(data.token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  }, []);

  const register = useCallback(async (name, email, password, extra = {}) => {
    try {
      const { data } = await api.post("/auth/register", { name, email, password, ...extra });
      setInMemoryToken(data.token);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  }, []);

  const confirmAge = useCallback(async () => {
    try {
      const { data } = await api.post("/auth/confirm-age", { confirmed: true });
      setUser(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // Cookie may already be expired or server unreachable; still clear local state.
      if (typeof console !== "undefined") console.debug("[auth] logout call failed:", e?.message);
    }
    setInMemoryToken(null);
    setUser(false);
  }, []);

  const value = useMemo(
    () => ({ user, bootstrapped, login, register, logout, confirmAge, refreshUser: fetchMe }),
    [user, bootstrapped, login, register, logout, confirmAge, fetchMe],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  return useContext(AuthCtx);
}
